import { FormEvent } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import { Component } from "react";
import { IUserState } from "../../Core/State/IUserState";
import { ILoginConflictState } from "../../Core/State/ILoginConflictState";
import styles from '../../assets/css/components/Login.module.css';
import icon from '/images/icon.svg';
import logo from '/images/logo-text.svg';
import desktopVideo from '/images/login/desktop-video.webm';
import mobileVideo from '/images/login/mobile-video.webm';
import desktopPlaceholder from '/images/login/desktop-placeholder.jpg';
import mobilePlaceholder from '/images/login/mobile-placeholder.jpg';
import noonieBlackVideo from '/images/login/noonie-black-video.webm';
import noonieWhiteVideo from '/images/login/noonie-white-video.webm';
import nooniePinkVideo from '/images/login/noonie-pink-video.webm';
import noonieBlackPlaceholder from '/images/login/noonie-black-placeholder.jpg';
import noonieWhitePlaceholder from '/images/login/noonie-white-placeholder.jpg';
import nooniePinkPlaceholder from '/images/login/noonie-pink-placeholder.jpg';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import didYouKnowIcon from '/images/login/did-you-know.svg';
import { isMobile } from 'react-device-detect';
import { t } from '../../i18n';
import LanguageSelector from '../Header/LanguageSelector';
import DuplicateSessionLoginModal from './DuplicateSessionLoginModal';

import {
    Button,
    TextField,
    Container,
    Grid,
    Typography,
    Stack,
    InputAdornment,
    IconButton,
    Box,
} from "@mui/material";

interface LoginProps {
    login: (username: string, password: string) => void;
    userState: IUserState;
    loginConflict?: ILoginConflictState | null;
    onConfirmLoginTakeover: (conflictId: string) => void;
    onCancelLoginTakeover: (conflictId: string) => void;
}

interface LoginState {
    username: string;
    password: string;
    hasCredentialError: boolean;
    isLoading: boolean;
    showPassword: boolean;
    selectedNoonieVideo: string;
    selectedNooniePlaceholder: string;
    currentDidYouKnowIndex: number;
    isTextVisible: boolean;
}

export default class Login extends Component<LoginProps, LoginState> {

    private didYouKnowInterval: NodeJS.Timeout | null = null;

    private get didYouKnowTexts() {
        return [
            t('Login:facts.tilt'),
            t('Login:facts.televised'),
            t('Login:facts.deadMansHand')
        ];
    }

    get allowLogin() {
        return import.meta.env.VITE_ALLOW_LOGIN !== 'false';
    }


    constructor(props: LoginProps) {
        super(props);
        
        // Randomly select a noonie video
        const noonieVideos = [
            { video: noonieBlackVideo, placeholder: noonieBlackPlaceholder },
            { video: noonieWhiteVideo, placeholder: noonieWhitePlaceholder },
            { video: nooniePinkVideo, placeholder: nooniePinkPlaceholder }
        ];
        
        const randomIndex = Math.floor(Math.random() * noonieVideos.length);
        const selectedNoonie = noonieVideos[randomIndex];
        
        this.state = {
            username: "",
            password: "",
            hasCredentialError: false,
            isLoading: false,
            showPassword: false,
            selectedNoonieVideo: selectedNoonie.video,
            selectedNooniePlaceholder: selectedNoonie.placeholder,
            currentDidYouKnowIndex: 0,
            isTextVisible: true
        };
    }

    componentDidMount() {
        // Start the did you know text rotation
        this.startDidYouKnowRotation();
    }

    componentWillUnmount() {
        // Clean up the interval when component unmounts
        if (this.didYouKnowInterval) {
            clearInterval(this.didYouKnowInterval);
        }
    }

    startDidYouKnowRotation = () => {
        this.didYouKnowInterval = setInterval(() => {
            this.setState({ isTextVisible: false }, () => {
                setTimeout(() => {
                    this.setState(prevState => ({
                        currentDidYouKnowIndex: (prevState.currentDidYouKnowIndex + 1) % this.didYouKnowTexts.length,
                        isTextVisible: true
                    }));
                }, 500); // Half second fade out, then change text and fade in
            });
        }, 30000); // 30 seconds
    };

    componentDidUpdate(prevProps: LoginProps) {
        if (!prevProps.userState.hasCredentialError && this.props.userState.hasCredentialError) {
            this.setState({ isLoading: false });
        }
        if (prevProps.loginConflict !== this.props.loginConflict && this.state.isLoading) {
            this.setState({ isLoading: false });
        }
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
            info: {
                main: "#ffffff",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    handleLogin = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (this.allowLogin) {
            this.setState({ isLoading: true });
            try {
                this.props.login(this.state.username, this.state.password);
            } catch (error) {
                this.setState({ isLoading: false });
            }
        }
    };

    handleClickShowPassword = () => this.setState((prevState) => ({ showPassword: !prevState.showPassword }));

    render() {
        const { username, password, isLoading, showPassword } = this.state;
        const { hasFocedLogoutMaxClients, hasCredentialError } = this.props.userState;

        return (
            <ThemeProvider theme={this.darkTheme}>
                <Grid
                    container
                    className={styles.login}
                    style={{ height: "100dvh" }}
                    alignItems="center"
                    justifyContent="center"
                >
                    <div className={styles.gradient}></div>
                    {/* Desktop Video Background */}
                    <video
                        className={styles.desktopVideo}
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster={desktopPlaceholder}
                    >
                        <source src={desktopVideo} type="video/webm" />
                    </video>
                    {/* Mobile Video Background */}
                    <video
                        className={styles.mobileVideo}
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster={mobilePlaceholder}
                    >
                        <source src={mobileVideo} type="video/webm" />
                    </video>
                    <Container>
                        <Box className={styles.didYouKnowBox}>
                            <img src={didYouKnowIcon} alt="Did You Know?" height={39} style={{ marginBottom: '0.5rem' }} />
                            <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 900, color: '#FFFFFF', mb: 2 }}>{t('Login:didYouKnow')}</Typography>
                            <Typography 
                                variant="body2" 
                                className={styles.didYouKnowText}
                                sx={{ 
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: '#FFFFFF',
                                    textAlign: 'center',
                                    lineHeight: 1.4,
                                    opacity: this.state.isTextVisible ? 0.5 : 0,
                                    transition: 'opacity 0.5s ease-in-out'
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: this.didYouKnowTexts[this.state.currentDidYouKnowIndex]
                                }}
                            />
                        </Box>
                        <Stack direction="row" justifyContent='center' alignItems='center' sx={{ width: "100%", textAlign: "center", mb: 3 }}>
                            <img src={icon} alt="PokerSolutions Icon" height={43} style={{ marginRight: '1rem' }} />
                            <img src={logo} alt="PokerSolutions Logo" height={24} />
                        </Stack>
                        <Stack direction="row" className={styles.container} sx={{ position: 'relative' }}>
                            {/* Language Selector - Top Right of Container */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    zIndex: 10,
                                }}
                            >
                                <LanguageSelector variant="icon" />
                            </Box>
                            <Stack
                                className={styles.left}
                                spacing={3}
                                sx={{
                                    py: 8,
                                    minHeight: this.props.loginConflict
                                        ? { xs: 'auto', md: '484px' }
                                        : undefined,
                                    justifyContent: this.props.loginConflict ? 'center' : 'flex-start',
                                }}
                            >
                                {this.props.loginConflict ? (
                                    <DuplicateSessionLoginModal
                                        conflict={this.props.loginConflict}
                                        onConfirm={this.props.onConfirmLoginTakeover}
                                        onCancel={this.props.onCancelLoginTakeover}
                                    />
                                ) : (
                                    <>
                                <Typography className={styles.title}>{t('Login:title')}</Typography>
                                <form onSubmit={this.handleLogin}>
                                    <TextField
                                        error={hasCredentialError}
                                        helperText={hasCredentialError ? t('Login:invalidCredentials') : "" }
                                        hiddenLabel
                                        placeholder={t('Login:usernamePlaceholder')}
                                        id="username"
                                        name="username"
                                        autoComplete="username"
                                        autoFocus
                                        required
                                        fullWidth
                                        value={username}
                                        onChange={(e) => this.setState({ username: e.target.value })}
                                        InputProps={{
                                            startAdornment: <InputAdornment className={styles.inputAdornment} position="start"><PersonIcon sx={{ color: '#828282' }} /></InputAdornment>
                                        }}
                                        variant="outlined"
                                        sx={{
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                paddingLeft: 0,
                                                backgroundColor: '#272932',
                                                '& fieldset': {
                                                    borderColor: '#272932', // Default border color
                                                    borderRadius: '4px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'hsla(0,0%,100%,.1)', // Hover border color
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'hsla(0,0%,100%,.1)', // Focused border color
                                                },
                                            },
                                            '& .MuiFormHelperText-root': {
                                                mx: 0,
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                px: 2,
                                                color: 'white',
                                                '&::placeholder': {
                                                    color: '#B5B5B5',
                                                    opacity: 1
                                                }
                                            },
                                        }}
                                        />
                                    <TextField
                                        error={hasCredentialError}
                                        helperText={(
                                            <span style={{ display: "inline-flex", flexDirection: "row", width: "100%", flexWrap: "wrap" }}>
                                                {hasCredentialError && (
                                                    <span style={{ marginRight: "auto" }}>
                                                        {t('Login:invalidCredentials')}
                                                    </span>
                                                )}
                                                <a href="#" target="_blank" style={{ color: "white", textDecoration: "underline" }}>
                                                    {t('Login:forgotPassword')}
                                                </a>
                                            </span>
                                        )}
                                        hiddenLabel
                                        placeholder={t('Login:passwordPlaceholder')}
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        required
                                        fullWidth
                                        value={password}
                                        onChange={(e) => this.setState({ password: e.target.value })}
                                        InputProps={{
                                            startAdornment: <InputAdornment className={styles.inputAdornment} position="start"><LockIcon sx={{ color: '#828282' }} /></InputAdornment>,
                                            endAdornment: <InputAdornment position="end"><IconButton onClick={this.handleClickShowPassword} edge="end" aria-label={showPassword ? t('Login:hidePassword') : t('Login:showPassword')}>{showPassword ? <VisibilityIcon sx={{ color: 'white' }} /> : <VisibilityOffIcon sx={{ color: '#828282' }} />}</IconButton></InputAdornment>
                                        }}
                                        variant="outlined"
                                        sx={{
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                paddingLeft: 0,
                                                backgroundColor: '#272932',
                                                '& fieldset': {
                                                    borderColor: '#272932', // Default border color
                                                    borderRadius: '4px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'hsla(0,0%,100%,.1)', // Hover border color
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'hsla(0,0%,100%,.1)', // Focused border color
                                                },
                                            },
                                            '& .MuiFormHelperText-root': {
                                                mx: 0,
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                px: 2,
                                                color: 'white',
                                                '&::placeholder': {
                                                    color: '#B5B5B5',
                                                    opacity: 1
                                                }
                                            },
                                        }}
                                        />
                                    {hasFocedLogoutMaxClients && (
                                        <Alert severity="error" sx={{ mb: 3 }}>
                                            {t('Login:forcedLogout')}
                                        </Alert>
                                    )}
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        disabled={isLoading || !this.allowLogin}
                                        style={{ textTransform: "capitalize" }}
                                        sx={{ mb: 1, py: 2 }}
                                        startIcon={ isLoading ? <RefreshIcon className={styles.loading} /> : <VerifiedUserIcon /> }
                                    >
                                        {this.allowLogin ? t('Login:loginButton') : t('Login:loginDisabled') }
                                    </Button>
                                    <Typography align="center">{t('Login:noAccount')} <a href="#" target="_blank" style={{ color: "white" }}>{t('Login:register')}</a></Typography>
                                </form>
                                    </>
                                )}
                            </Stack>
                            <Stack 
                                className={styles.right}
                                sx={{
                                    display: {
                                        xs: 'none',
                                        sm: 'none',
                                        md: 'flex'
                                    }
                                }}
                            >
                                <video
                                    className={styles.noonieVideo}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    poster={this.state.selectedNooniePlaceholder}
                                >
                                    <source src={this.state.selectedNoonieVideo} type="video/webm" />
                                </video>
                            </Stack>
                        </Stack>
                    </Container>
                </Grid>
            </ThemeProvider>
        );
    }
};
